import { useAuth0 } from '@auth0/auth0-react'
import CheckIcon from '@mui/icons-material/Check'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import HowToVoteOutlinedIcon from '@mui/icons-material/HowToVoteOutlined'
import IosShareIcon from '@mui/icons-material/IosShare'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router'
import { logEvent } from '../../../analytics/analytics'
import RestaurantCard from '../../../components/restaurant/RestaurantCard'
import EmptyScreen from '../../../components/EmptyScreen'
import DineSessionChat from './DineSessionChat'
import { ensureAnonId } from '../../../helpers/anon-id'
import { getLatLngFromAddress } from '../../../helpers/location-helper'
import {
  clearPendingGroupSessionIntent,
  setPendingGroupSessionIntent,
} from '../../../helpers/groupSessionIntent'
import { getWebOrigin } from '../../../helpers/referral-helper'
import { ROUTES } from '../../../helpers/routes-helper'
import { DEFAULT_CARD_IMAGE } from '../../../helpers/static-constants'
import useAppUtilities from '../../../hooks/useAppUtilities'
import {
  castVote,
  fetchSessionCandidates,
  SessionCandidate,
  deleteGroupSession,
  fetchGroupSessionRecommendations,
  fetchSessionTally,
  joinGroupSession,
  persistGroupSession,
  resolveGroupSession,
  submitRsvp,
  submitSessionTaste,
  updateSessionSearch,
  GroupSession,
  RsvpStatus,
  RsvpSummary,
  SessionTally,
} from '../../../services/groupSessionService'
import { RestaurantAndPredictedRestaurant } from '../../../types'
import { errorLog } from '../../../utils/log-helper'
import {
  ambience,
  cuisines,
  dietaryPreferences,
} from '../../../helpers/onboard-helper'
import OnboardSearchBox from '../onboardPages/OnboardSearch'

// Single source of truth: the session taste picker reuses the SAME canonical onboarding vocabulary
// (helpers/onboard-helper.ts) that the onboarding flow renders, so a taste set picked here is
// identical to one picked in onboarding — no drift, no mixed "Mexican" vs "mexican" in a profile.
// Those arrays are LOWERCASE; the model matches case-insensitively and the UI Title-cases via
// `titleCase`.
const CUISINE_OPTIONS = cuisines
const VIBE_OPTIONS = ambience
// 'no restrictions' is a UI-only sentinel (filtered out before submit), not a stored value.
const DIETARY_OPTIONS = ['no restrictions', ...dietaryPreferences]

/** Title-case a lowercase taste token for display (e.g. "gluten-free" -> "Gluten-Free"). */
const titleCase = (s: string): string =>
  s.replace(/\b\w/g, (c) => c.toUpperCase())

/** Auto-refresh interval so the group picks update live as members contribute. */
const RECS_REFRESH_MS = 20000

/** RSVP options rendered as a segmented button row. Order = strongest to weakest commitment. */
const RSVP_OPTIONS: Array<{ status: RsvpStatus; label: string }> = [
  { status: 'GOING', label: 'Going' },
  { status: 'MAYBE', label: 'Maybe' },
  { status: 'DECLINED', label: "Can't make it" },
]

/**
 * Multi-select chip row for the 3-tap taste input. When `indexKey` + `onAdd` are supplied it also
 * renders the shared onboarding ES-backed typeahead (via resactly-service `/search/options`), so a
 * member can free-text add a taste that isn't one of the default chips — parity with onboarding.
 * Custom-added values (not in `options`) render as extra chips so they stay visible and removable.
 */
const ChipGroup: React.FC<{
  label: string
  options: string[]
  selected: string[]
  onToggle: (value: string) => void
  indexKey?: string
  onAdd?: (value: string) => void
}> = ({ label, options, selected, onToggle, indexKey, onAdd }) => {
  const allOptions = [
    ...options,
    ...selected.filter((s) => !options.includes(s)),
  ]
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {label}
      </Typography>
      {indexKey && onAdd && (
        <Box sx={{ mb: 1 }}>
          <OnboardSearchBox
            indexKey={indexKey}
            setWord={onAdd}
            searchWidth="100%"
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {allOptions.map((opt) => {
          const isOn = selected.includes(opt)
          return (
            <Chip
              key={opt}
              label={titleCase(opt)}
              clickable
              color={isOn ? 'primary' : 'default'}
              variant={isOn ? 'filled' : 'outlined'}
              onClick={() => onToggle(opt)}
            />
          )
        })}
      </Box>
    </Box>
  )
}

/**
 * Group Dining Session page (/dine/:shareToken) — the anon-friendly join + live-recommendations
 * screen. No auth wall: we resolve the session by token, anonymously join (idempotent), collect a
 * 3-tap taste input, and render group recommendations that update as members contribute. The signup
 * CTA is a soft gate at the value peak — it stashes a pending intent and resumes into the session
 * after signup (skipping onboarding). See resactly-dis/docs/GROUP_VIRAL_SESSIONS_PLAN.md.
 */
const DineSessionPage: React.FC = () => {
  const { shareToken = '' } = useParams()
  const { navigate } = useAppUtilities()
  const { isAuthenticated, loginWithRedirect } = useAuth0()

  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState<GroupSession | null>(null)
  const [notFound, setNotFound] = useState(false)
  // When the caller could NOT become an active member (party full / expired between resolve and
  // join), participation is a dead end — we lock the controls and show an explicit banner instead
  // of a fully interactive page that silently 403s. Null = the caller is (or became) a member.
  const [joinBlockedReason, setJoinBlockedReason] = useState<
    'party_full' | 'unavailable' | null
  >(null)

  const [cuisines, setCuisines] = useState<string[]>([])
  const [vibe, setVibe] = useState<string[]>([])
  const [dietary, setDietary] = useState<string[]>([])
  const [tasteSubmitting, setTasteSubmitting] = useState(false)
  const [tasteSubmitted, setTasteSubmitted] = useState(false)

  const [recs, setRecs] = useState<RestaurantAndPredictedRestaurant[]>([])
  // Planner picks: restaurants members explicitly ADDED via "Plan with friends", as opposed to
  // the geo/taste-derived recommendations below. Rendered first — a curated shortlist is the whole
  // point of the planner, and invitees vote on it exactly like a recommendation.
  const [picks, setPicks] = useState<SessionCandidate[]>([])
  const [recsLoading, setRecsLoading] = useState(false)
  // True when the location has no local restaurants yet and the backend is filling coverage; drives
  // the "we're finding restaurants near {location}" banner.
  const [coverageExpanding, setCoverageExpanding] = useState(false)
  // True when the group has dietary needs but no restaurant can satisfy all of them; drives the
  // "no places match everyone's dietary needs" banner.
  const [dietaryUnmatched, setDietaryUnmatched] = useState(false)

  // True when the session has a cuisine focus but no restaurant nearby matches it; drives the
  // "no {cuisine} restaurants nearby" banner.
  const [cuisineUnmatched, setCuisineUnmatched] = useState(false)

  // Owner-only in-session edit of the candidate-search inputs (location + cuisine focus).
  const [editingSearch, setEditingSearch] = useState(false)
  const [editLocation, setEditLocation] = useState('')
  const [editCuisines, setEditCuisines] = useState<string[]>([])
  const [searchUpdating, setSearchUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [recommendationId, setRecommendationId] = useState<string | undefined>()
  const [copied, setCopied] = useState(false)
  const [persisting, setPersisting] = useState(false)
  const [persisted, setPersisted] = useState(false)
  const [persistMsg, setPersistMsg] = useState<string | null>(null)

  // Phase 3 — RSVP + voting + "the pick".
  const [yourRsvp, setYourRsvp] = useState<RsvpStatus | undefined>()
  const [rsvpSummary, setRsvpSummary] = useState<RsvpSummary | undefined>()
  const [rsvpSubmitting, setRsvpSubmitting] = useState<RsvpStatus | null>(null)
  const [tally, setTally] = useState<SessionTally | null>(null)
  const [yourVote, setYourVote] = useState<string | undefined>()
  const [votingAlias, setVotingAlias] = useState<string | undefined>()

  const openedFiredRef = useRef(false)
  const joinedFiredRef = useRef(false)
  const recsViewedFiredRef = useRef(false)
  const pickViewedFiredRef = useRef(false)

  // In-app notification cues — lightweight, driven by the existing ~20s poll (not push/email, which
  // anon guests have no contact info for). Refs track the last-seen state so a cue fires only on a
  // genuine transition (a friend joined / picks became available / the pick emerged), never on the
  // initial load and never from the current device's own actions.
  const prevMemberCountRef = useRef<number | null>(null)
  const hadRecsRef = useRef(false)
  const pickCueFiredRef = useRef(false)
  const cuesArmedRef = useRef(false)
  const [cue, setCue] = useState<string | null>(null)

  const pushCue = useCallback(
    (
      type: 'members_joined' | 'recs_ready' | 'pick_in',
      text: string,
      memberDelta?: number,
    ): void => {
      setCue(text)
      logEvent('session_cue_shown', {
        share_token: shareToken,
        cue_type: type,
        member_delta: memberDelta,
        screen_name: 'dine_session',
      })
    },
    [shareToken],
  )

  // Use the canonical web origin (never capacitor://localhost on native) so the invite link a
  // recipient receives actually opens — universal/app links route it back into the app.
  const shareUrl =
    typeof window !== 'undefined'
      ? `${getWebOrigin()}${ROUTES.DINE}/${shareToken}`
      : `${ROUTES.DINE}/${shareToken}`

  // Resume-into-session guard for ANY auth entry. handleSignupCta sets this before the in-session
  // signup CTA, but a generic login (the nav "Log in / Sign up" button via useAuthHelper, etc.) does
  // not — so a new account created that way fell through to the isNewUser -> onboarding redirect
  // instead of returning to the session. While an anonymous user is viewing a valid session, stash
  // the group-session resume intent so BaseLayout lands them back here after auth regardless of which
  // button they used. BaseLayout consumes/clears it on resume; save/points intents still take
  // priority in BaseLayout's ordering if the user later starts one of those flows.
  useEffect(() => {
    if (!isAuthenticated && shareToken && session && !notFound) {
      setPendingGroupSessionIntent({
        shareToken,
        returnRoute: `${ROUTES.DINE}/${shareToken}`,
      })
    }
  }, [isAuthenticated, shareToken, session, notFound])

  const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  const toggle = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    value: string,
  ): void => {
    setter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    )
  }

  /**
   * Add-only (union) handler for the ES-backed typeahead — normalizes to the lowercase canonical
   * vocabulary so a free-text add matches the model / onboarding tokens; never removes.
   */
  const addTaste = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    value: string,
  ): void => {
    const v = value.trim().toLowerCase()
    if (!v) return
    setter((prev) => (prev.includes(v) ? prev : [...prev, v]))
  }

  const loadRecs = useCallback(
    async (
      memberCount?: number,
    ): Promise<RestaurantAndPredictedRestaurant[]> => {
      if (!shareToken) return []
      try {
        // Curated picks and derived recommendations load together — a planner with picks but no
        // recommendations (a brand-new draft with no location) must still render its shortlist.
        void fetchSessionCandidates(shareToken).then((c) => {
          if (c.valid) setPicks(c.candidates)
        })
        const data = await fetchGroupSessionRecommendations(shareToken)
        const result = data.result ?? []
        setRecs(result)
        setCoverageExpanding(!!data.broadened && result.length === 0)
        setDietaryUnmatched(!!data.dietaryUnmatched && result.length === 0)
        setCuisineUnmatched(!!data.cuisineUnmatched && result.length === 0)
        setRecommendationId(data.recommendationId)
        if (!recsViewedFiredRef.current) {
          recsViewedFiredRef.current = true
          logEvent('group_recs_viewed', {
            share_token: shareToken,
            result_count: result.length,
            member_count: memberCount,
            recommendation_id: data.recommendationId,
            screen_name: 'dine_session',
          })
        }
        return result
      } catch (e) {
        errorLog('[DineSessionPage] load recs failed', e)
        return []
      }
    },
    [shareToken],
  )

  // Live vote tally + the winning pick. Keeps rsvpSummary in sync (the tally response carries it)
  // and fires session_pick_viewed once, when a winner first emerges.
  const loadTally = useCallback(async (): Promise<SessionTally | null> => {
    if (!shareToken) return null
    try {
      const anonId = await ensureAnonId()
      const data = await fetchSessionTally(shareToken, anonId)
      if (!data.valid) return null
      setTally(data)
      if (data.rsvpSummary) setRsvpSummary(data.rsvpSummary)
      // Hydrate the caller's own prior vote/RSVP so a returning participant sees their choice
      // reflected in the buttons (the aggregate tally already counted them).
      setYourVote(data.yourVote ?? undefined)
      setYourRsvp(data.yourRsvp ?? undefined)
      if (data.winner && !pickViewedFiredRef.current) {
        pickViewedFiredRef.current = true
        logEvent('session_pick_viewed', {
          share_token: shareToken,
          winner_alias: data.winner,
          total_votes: data.totalVotes,
          screen_name: 'dine_session',
        })
      }
      return data
    } catch (e) {
      errorLog('[DineSessionPage] load tally failed', e)
      return null
    }
  }, [shareToken])

  // Resolve -> join (idempotent, anon) -> load recs.
  useEffect(() => {
    let cancelled = false
    const init = async (): Promise<void> => {
      if (!shareToken) {
        setNotFound(true)
        setLoading(false)
        return
      }
      setLoading(true)
      const anonIdForResolve = await ensureAnonId()
      const resolved = await resolveGroupSession(shareToken, anonIdForResolve)
      if (cancelled) return
      if (!resolved.valid) {
        setNotFound(true)
        setLoading(false)
        return
      }
      setSession(resolved)
      setLoading(false)
      if (!openedFiredRef.current) {
        openedFiredRef.current = true
        logEvent('session_opened', {
          share_token: shareToken,
          member_count: resolved.memberCount,
          screen_name: 'dine_session',
        })
      }
      // Seed the member-count baseline from the resolve so live-join cues measure from here.
      prevMemberCountRef.current = resolved.memberCount
      try {
        const anonId = await ensureAnonId()
        const joinResult = await joinGroupSession(shareToken, anonId)
        if (cancelled) return
        if (joinResult.joined) {
          // Our own join must not later read as "a friend joined".
          prevMemberCountRef.current = joinResult.memberCount
          if (isAuthenticated) {
            // Signing in mid-session claims this device's guest identity (transfers ownership +
            // membership server-side). Re-resolve so isOwner/canPersist reflect that the signed-in
            // user is now the owner — otherwise the owner controls wouldn't appear until reload.
            const refreshed = await resolveGroupSession(shareToken, anonId)
            if (!cancelled && refreshed.valid) {
              setSession(refreshed)
              prevMemberCountRef.current = refreshed.memberCount
            }
          } else {
            setSession((prev) =>
              prev ? { ...prev, memberCount: joinResult.memberCount } : prev,
            )
          }
          setJoinBlockedReason(null)
          // A successful (active-member) join — the counterpart to session_join_failed. Fired ONCE
          // per page mount, guarded so the effect re-running on the isAuthenticated dep flip
          // (anon -> login mid-session) doesn't double-emit. The creator's own load also returns
          // joined:true (idempotent owner membership), so this fires for the owner too — is_owner /
          // role let analysis filter owner vs. invited-member joins.
          if (!joinedFiredRef.current) {
            joinedFiredRef.current = true
            logEvent('session_joined', {
              share_token: shareToken,
              member_count: joinResult.memberCount,
              is_owner: resolved.isOwner,
              role: resolved.isOwner ? 'owner' : 'member',
              is_authed: isAuthenticated,
              screen_name: 'dine_session',
            })
          }
        } else {
          // Party full (cap) or no longer available — a designed limit / expiry, not a bug. Lock
          // participation and make it measurable rather than serving a dead interactive page.
          const reason: 'party_full' | 'unavailable' =
            joinResult.message && /full/i.test(joinResult.message)
              ? 'party_full'
              : 'unavailable'
          setJoinBlockedReason(reason)
          logEvent('session_join_failed', {
            share_token: shareToken,
            reason,
            member_count: joinResult.memberCount || resolved.memberCount,
            screen_name: 'dine_session',
          })
        }
      } catch (e) {
        errorLog('[DineSessionPage] join failed', e)
      }
      setRecsLoading(true)
      const [initialRecs, initialTally] = await Promise.all([
        loadRecs(resolved.memberCount),
        loadTally(),
      ])
      if (!cancelled) {
        setRecsLoading(false)
        // Seed cue baselines so nothing already-present on load pops a cue; arm for the poll.
        hadRecsRef.current = initialRecs.length > 0
        pickCueFiredRef.current = !!initialTally?.winner
        cuesArmedRef.current = true
      }
    }
    void init()
    return () => {
      cancelled = true
    }
    // isAuthenticated: re-run on login so the join fires with the JWT and claims the guest identity
    // (ownership transfer) instead of leaving a phantom guest owner.
  }, [shareToken, isAuthenticated, loadRecs, loadTally])

  // Live refresh: re-resolve (member count) + reload recs on an interval.
  useEffect(() => {
    if (!session?.valid) return
    const id = setInterval(() => {
      void (async () => {
        const resolved = await resolveGroupSession(shareToken)
        if (resolved.valid) {
          setSession((prev) =>
            prev ? { ...prev, memberCount: resolved.memberCount } : resolved,
          )
        }
        const [polledRecs, polledTally] = await Promise.all([
          loadRecs(resolved.memberCount),
          loadTally(),
        ])

        if (!cuesArmedRef.current) return
        // A friend joined — fire on any real increase vs. the last-seen count.
        const prevCount = prevMemberCountRef.current
        if (
          resolved.valid &&
          prevCount != null &&
          resolved.memberCount > prevCount
        ) {
          const delta = resolved.memberCount - prevCount
          pushCue(
            'members_joined',
            delta === 1 ? '1 friend joined' : `${delta} friends joined`,
            delta,
          )
        }
        if (resolved.valid) prevMemberCountRef.current = resolved.memberCount
        // Recommendations became available (0 -> some).
        if (!hadRecsRef.current && polledRecs.length > 0) {
          hadRecsRef.current = true
          pushCue('recs_ready', 'Your group’s picks are ready!')
        }
        // The winning pick emerged.
        if (!pickCueFiredRef.current && polledTally?.winner) {
          pickCueFiredRef.current = true
          pushCue('pick_in', 'Your group’s pick is in!')
        }
      })()
    }, RECS_REFRESH_MS)
    return () => clearInterval(id)
  }, [session?.valid, shareToken, loadRecs, loadTally, pushCue])

  const handleSubmitTaste = async (): Promise<void> => {
    if (!shareToken || joinBlockedReason) return
    setTasteSubmitting(true)
    try {
      const anonId = await ensureAnonId()
      const updated = await submitSessionTaste({
        shareToken,
        anonId,
        cuisines: cuisines.join(', ') || undefined,
        vibe: vibe.join(', ') || undefined,
        dietary:
          dietary.filter((d) => d !== 'no restrictions').join(', ') ||
          undefined,
      })
      if (updated.valid) {
        // Our own contribution may bump the count — keep the baseline in sync so it doesn't read
        // as "a friend joined" on the next poll.
        prevMemberCountRef.current = updated.memberCount
        setSession((prev) =>
          prev ? { ...prev, memberCount: updated.memberCount } : updated,
        )
      }
      setTasteSubmitted(true)
      logEvent('session_taste_submitted', {
        share_token: shareToken,
        cuisine_count: cuisines.length,
        has_vibe: vibe.length > 0,
        has_dietary: dietary.some((d) => d !== 'no restrictions'),
        screen_name: 'dine_session',
      })
      setRecsLoading(true)
      // Re-fire the recs-viewed event after a contribution so live updates are measurable.
      recsViewedFiredRef.current = false
      await loadRecs(updated.memberCount)
      setRecsLoading(false)
    } catch (e) {
      errorLog('[DineSessionPage] submit taste failed', e)
    } finally {
      setTasteSubmitting(false)
    }
  }

  /** Owner-only: delete the session (with a confirm), then leave for the Dine landing. */
  const handleDeleteSession = async (): Promise<void> => {
    if (!shareToken || deleting) return
    if (
      !window.confirm(
        'Delete this dinner session? This removes it for everyone and can’t be undone.',
      )
    ) {
      return
    }
    setDeleting(true)
    try {
      const anonId = await ensureAnonId()
      const ok = await deleteGroupSession(shareToken, anonId)
      if (ok) {
        logEvent('session_deleted', {
          share_token: shareToken,
          screen_name: 'dine_session',
        })
        navigate(ROUTES.DINE)
      } else {
        setSearchError('Could not delete this dinner. Please try again.')
        setDeleting(false)
      }
    } catch (e) {
      errorLog('[DineSessionPage] delete session failed', e)
      setSearchError('Could not delete this dinner. Please try again.')
      setDeleting(false)
    }
  }

  /** Prefill the owner search editor from the session's current location + cuisine focus. */
  const openSearchEditor = (): void => {
    setEditLocation(session?.location ?? '')
    const preset = (session?.searchTerm ?? '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
    const matched = CUISINE_OPTIONS.filter((opt) =>
      preset.some((p) => p.toLowerCase() === opt.toLowerCase()),
    )
    setEditCuisines(matched)
    setSearchError(null)
    setEditingSearch(true)
  }

  /**
   * Owner-only: change what the session searches for. Geocodes the typed location with the same
   * helper the search flow uses, PATCHes the session (which evicts the group caches server-side),
   * then re-fetches the recommendations so the picks reflect the new location/cuisine immediately.
   */
  const handleUpdateSearch = async (): Promise<void> => {
    if (!shareToken) return
    if (!editLocation.trim()) {
      setSearchError('Add a location so we can find restaurants nearby.')
      return
    }
    setSearchUpdating(true)
    setSearchError(null)
    try {
      const anonId = await ensureAnonId()
      // A failed geocode leaves coords undefined → backend falls back to the legacy city-text search.
      const coords = await getLatLngFromAddress(editLocation.trim())
      const updated = await updateSessionSearch({
        shareToken,
        anonId,
        location: editLocation.trim(),
        latitude: coords?.lat,
        longitude: coords?.lng,
        searchTerm: editCuisines.join(', '),
      })
      if (!updated.valid) {
        setSearchError(
          updated.message ?? 'Could not update the search. Please try again.',
        )
        return
      }
      setSession((prev) => (prev ? { ...prev, ...updated } : updated))
      logEvent('session_search_updated', {
        share_token: shareToken,
        has_coords: !!coords,
        cuisine_count: editCuisines.length,
        screen_name: 'dine_session',
      })
      setEditingSearch(false)
      setRecsLoading(true)
      recsViewedFiredRef.current = false
      await loadRecs(updated.memberCount)
      setRecsLoading(false)
    } catch (e) {
      errorLog('[DineSessionPage] update search failed', e)
      setSearchError('Could not update the search. Please try again.')
    } finally {
      setSearchUpdating(false)
    }
  }

  const handleRsvp = async (status: RsvpStatus): Promise<void> => {
    if (!shareToken || joinBlockedReason) return
    setRsvpSubmitting(status)
    const prev = yourRsvp
    setYourRsvp(status) // optimistic
    try {
      const anonId = await ensureAnonId()
      const res = await submitRsvp(shareToken, anonId, status)
      if (res.valid) {
        setYourRsvp(res.yourRsvp ?? status)
        if (res.rsvpSummary) setRsvpSummary(res.rsvpSummary)
        prevMemberCountRef.current = res.memberCount
        setSession((p) => (p ? { ...p, memberCount: res.memberCount } : p))
        // Only log on a genuine RSVP — never poison the funnel with failed attempts.
        logEvent('session_rsvp_submitted', {
          share_token: shareToken,
          status,
          screen_name: 'dine_session',
        })
        await loadTally()
      } else {
        setYourRsvp(prev)
      }
    } catch (e) {
      setYourRsvp(prev)
      errorLog('[DineSessionPage] rsvp failed', e)
    } finally {
      setRsvpSubmitting(null)
    }
  }

  const handleVote = async (alias: string): Promise<void> => {
    if (!shareToken || !alias || joinBlockedReason) return
    setVotingAlias(alias)
    const prev = yourVote
    setYourVote(alias) // optimistic
    try {
      const anonId = await ensureAnonId()
      const res = await castVote(shareToken, anonId, alias)
      if (res.valid) {
        setYourVote(res.restaurantAlias ?? alias)
        // Resolve the voted restaurant's id from the loaded recs by alias (same lookup pattern as
        // winnerRec) so the vote joins to dim_restaurant, not just the alias. The vote handler only
        // receives the alias, but recs is in scope and already carries the id — no signature change.
        const votedRestaurantId =
          recs.find((r) => r.restaurantDTO?.alias === alias)?.restaurantDTO
            ?.restaurantId ??
          picks.find((p) => p.restaurantAlias === alias)?.restaurantId
        // Only log a genuine vote — never poison the funnel with failed attempts.
        logEvent('session_vote_cast', {
          share_token: shareToken,
          restaurant_alias: alias,
          restaurant_id: votedRestaurantId,
          screen_name: 'dine_session',
        })
        await loadTally()
      } else {
        setYourVote(prev)
      }
    } catch (e) {
      setYourVote(prev)
      errorLog('[DineSessionPage] vote failed', e)
    } finally {
      setVotingAlias(undefined)
    }
  }

  const handleReserve = (rec: RestaurantAndPredictedRestaurant): void => {
    const r = rec.restaurantDTO
    const reservationUrl = r?.reservationUrl ?? r?.website
    if (!reservationUrl) return
    const destination: 'reservation_page' | 'website' | 'none' =
      r?.reservationUrl ? 'reservation_page' : r?.website ? 'website' : 'none'
    let destinationHost: string | undefined
    try {
      destinationHost = new URL(reservationUrl).host
    } catch {
      destinationHost = undefined
    }
    logEvent('reservation_click', {
      restaurant_id: r?.restaurantId ?? '',
      restaurant_alias: r?.alias ?? undefined,
      destination,
      destination_host: destinationHost,
      recommendation_id: recommendationId,
      // The planner this decision came out of — closes the planner -> restaurant-decision join
      // that previously dead-ended because dine-surface reservation clicks carried no session key.
      share_token: shareToken,
      screen_name: 'dine_session',
    })
    window.open(reservationUrl, '_blank')
  }

  const handleCopy = async (): Promise<void> => {
    const nav = typeof navigator !== 'undefined' ? navigator : undefined
    // One clean, self-contained invite (text + URL, properly separated). Passing text + url
    // separately to navigator.share let the share sheet's "copy" target jam them together
    // (e.g. "…group picks:<url>" with the colon dangling). This is a Copy button, so copy the
    // formatted string directly; fall back to the native share sheet only if the clipboard is
    // unavailable, and hand it a single self-contained string so it can't re-jam.
    const shareText = `Add your taste and see our group picks: ${shareUrl}`
    try {
      if (nav?.clipboard?.writeText) {
        await nav.clipboard.writeText(shareText)
        setCopied(true)
        setTimeout(() => setCopied(false), 3000)
        logEvent('session_link_copied', {
          share_token: shareToken,
          method: 'copy_link',
          screen_name: 'dine_session',
        })
        return
      }
      if (nav?.share) {
        await nav.share({
          title: session?.name ?? 'Join my dinner on Resactly',
          text: shareText,
        })
        logEvent('session_link_copied', {
          share_token: shareToken,
          method: 'native',
          screen_name: 'dine_session',
        })
      }
    } catch (e) {
      errorLog('[DineSessionPage] share/copy failed', e)
    }
  }

  // Graduation bridge: a signed-in owner saves this ephemeral session as a persistent group.
  const handlePersist = async (): Promise<void> => {
    if (persisting || persisted) return
    setPersisting(true)
    try {
      const result = await persistGroupSession(shareToken)
      if (result.persisted) {
        setPersisted(true)
        setPersistMsg('Saved to your groups — you can plan together again.')
        logEvent('session_persisted', {
          share_token: shareToken,
          group_id: result.groupId,
          screen_name: 'dine_session',
        })
      } else {
        setPersistMsg(
          result.message ?? 'Only the organizer can save this group.',
        )
      }
    } catch (e) {
      errorLog('[DineSessionPage] persist failed', e)
      setPersistMsg('Could not save this group. Please try again.')
    } finally {
      setPersisting(false)
    }
  }

  const handleSignupCta = async (): Promise<void> => {
    const returnRoute = `${ROUTES.DINE}/${shareToken}`
    // Fresh intent: clear any stale one first so we always resume into THIS session.
    clearPendingGroupSessionIntent()
    setPendingGroupSessionIntent({ shareToken, returnRoute })
    try {
      sessionStorage.setItem('signup_source', 'group_session')
    } catch {
      // ignore
    }
    logEvent('session_signup_prompted', {
      share_token: shareToken,
      screen_name: 'dine_session',
    })
    logEvent('signup_started', {
      screen_name: 'dine_session',
    })
    try {
      await loginWithRedirect({
        authorizationParams: { screen_hint: 'signup' },
        appState: { returnTo: returnRoute },
      })
    } catch (e) {
      errorLog('[DineSessionPage] signup redirect failed', e)
    }
  }

  const voteCountFor = (alias?: string): number =>
    tally?.tallies.find((t) => t.restaurantAlias === alias)?.votes ?? 0

  const winnerRec =
    tally?.winner != null
      ? recs.find((r) => r.restaurantDTO?.alias === tally.winner)
      : undefined

  const winnerReservationUrl =
    winnerRec?.restaurantDTO?.reservationUrl ??
    winnerRec?.restaurantDTO?.website

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (notFound || !session) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <Helmet>
          <title>Resactly | Dinner not found</title>
        </Helmet>
        <EmptyScreen
          mainText="This dinner isn't available"
          secondaryText="The link may be invalid or the dinner may have expired."
          imageUrl="/images/empty-screen.svg"
          containerHeight="320px"
        />
        <Button
          variant="contained"
          onClick={() => navigate(ROUTES.DINE)}
          sx={{ mt: 2, textTransform: 'none', borderRadius: 2 }}
        >
          Plan your own dinner
        </Button>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      <Helmet>
        <title>{`Resactly | ${session.name ?? 'Group dinner'}`}</title>
        <meta
          name="description"
          content="Add your taste and see the restaurant picks your group will love."
        />
      </Helmet>

      {/* Session header */}
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.paper',
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {session.name ?? 'Group dinner'}
        </Typography>
        <Stack
          direction="row"
          spacing={2}
          flexWrap="wrap"
          sx={{ color: 'text.secondary', mb: 2 }}
        >
          {session.location && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <PlaceOutlinedIcon fontSize="small" />
              <Typography variant="body2">{session.location}</Typography>
            </Stack>
          )}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <GroupsOutlinedIcon fontSize="small" />
            <Typography variant="body2">
              {session.memberCount} joined
              {session.partyCap ? ` · up to ${session.partyCap}` : ''}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
        >
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              backgroundColor: 'action.hover',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {shareUrl}
          </Typography>
          <Button
            variant="contained"
            startIcon={
              copied ? (
                <CheckIcon />
              ) : canNativeShare ? (
                <IosShareIcon />
              ) : (
                <ContentCopyIcon />
              )
            }
            onClick={() => {
              void handleCopy()
            }}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {copied ? 'Copied!' : 'Share invite'}
          </Button>
        </Stack>
      </Box>

      {/* Party full / no longer available — the caller could not become an active member. Explicit
          dead-end state instead of a fully interactive page whose actions would silently 403. */}
      {joinBlockedReason && (
        <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }}>
          {joinBlockedReason === 'party_full'
            ? `This dinner is full — up to ${
                session.partyCap || ''
              } people can join. Ask the organizer to start another, or plan your own.`
            : 'This dinner is full or no longer available. You can still browse the group’s picks below.'}
        </Alert>
      )}

      {/* Graduation bridge: signed-in owner (or an adopting member of an orphan session) saves this
          session as a persistent group. Shown only while ephemeral (has a TTL) and not yet saved. */}
      {isAuthenticated &&
        session.valid &&
        session.canPersist &&
        (persisted || session.expiresAt) && (
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 3,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: 'background.paper',
              mb: 3,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {persisted
                    ? 'Saved to your groups'
                    : 'Keep this crew together?'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {persisted
                    ? 'Find it anytime under your groups.'
                    : 'Save this session as a group so you can plan your next dinner together.'}
                </Typography>
              </Box>
              {!persisted && (
                <Button
                  variant="outlined"
                  startIcon={<GroupsOutlinedIcon />}
                  disabled={persisting}
                  onClick={() => {
                    void handlePersist()
                  }}
                  sx={{ textTransform: 'none', borderRadius: 2, flexShrink: 0 }}
                >
                  {persisting ? 'Saving…' : 'Save this group'}
                </Button>
              )}
            </Stack>
          </Box>
        )}

      {/* RSVP — the coordination step: who's actually coming. */}
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.paper',
          mb: 3,
        }}
      >
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Can you make it?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Let the group know — you can change your answer anytime.
        </Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          {RSVP_OPTIONS.map(({ status, label }) => {
            const isOn = yourRsvp === status
            return (
              <Button
                key={status}
                variant={isOn ? 'contained' : 'outlined'}
                onClick={() => {
                  void handleRsvp(status)
                }}
                disabled={rsvpSubmitting !== null || !!joinBlockedReason}
                startIcon={
                  rsvpSubmitting === status ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : isOn ? (
                    <CheckIcon />
                  ) : undefined
                }
                sx={{ textTransform: 'none', borderRadius: 2, minWidth: 120 }}
              >
                {label}
              </Button>
            )
          })}
        </Stack>
        {rsvpSummary && (
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 2, color: 'text.secondary' }}
          >
            <Typography variant="body2">
              <strong>{rsvpSummary.going}</strong> going
            </Typography>
            <Typography variant="body2">
              <strong>{rsvpSummary.maybe}</strong> maybe
            </Typography>
            <Typography variant="body2">
              <strong>{rsvpSummary.declined}</strong> can&apos;t make it
            </Typography>
            <Typography variant="body2">
              <strong>{rsvpSummary.noResponse}</strong> no response
            </Typography>
          </Stack>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Taste input */}
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 3,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: 'background.paper',
            }}
          >
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Add your taste
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Three quick taps — the group&apos;s picks improve with every
              answer.
            </Typography>
            <Stack spacing={2.5}>
              <ChipGroup
                label="Cuisines you're feeling"
                options={CUISINE_OPTIONS}
                selected={cuisines}
                onToggle={(v) => toggle(setCuisines, v)}
                indexKey="REACT_APP_CUISINES_INDEX"
                onAdd={(v) => addTaste(setCuisines, v)}
              />
              <ChipGroup
                label="Vibe"
                options={VIBE_OPTIONS}
                selected={vibe}
                onToggle={(v) => toggle(setVibe, v)}
                indexKey="REACT_APP_AMBIENCE_INDEX"
                onAdd={(v) => addTaste(setVibe, v)}
              />
              <ChipGroup
                label="Dietary needs"
                options={DIETARY_OPTIONS}
                selected={dietary}
                onToggle={(v) => toggle(setDietary, v)}
                indexKey="REACT_APP_DIETARY_PREFERENCES_INDEX"
                onAdd={(v) => addTaste(setDietary, v)}
              />
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  void handleSubmitTaste()
                }}
                disabled={tasteSubmitting || !!joinBlockedReason}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {tasteSubmitting ? (
                  <CircularProgress size={22} color="inherit" />
                ) : tasteSubmitted ? (
                  'Update my taste'
                ) : (
                  'See our picks'
                )}
              </Button>
              {tasteSubmitted && (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  Thanks! Your picks are updated below as friends join.
                </Alert>
              )}
            </Stack>
          </Box>
        </Grid>

        {/* Group recommendations */}
        <Grid item xs={12} md={7}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="h6" fontWeight={700}>
              Your group&apos;s picks
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {recsLoading && <CircularProgress size={18} />}
              {session.isOwner && !editingSearch && (
                <Button
                  size="small"
                  startIcon={<TuneOutlinedIcon fontSize="small" />}
                  onClick={openSearchEditor}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Edit location & cuisine
                </Button>
              )}
              {session.isOwner && !editingSearch && (
                <Button
                  size="small"
                  color="error"
                  disabled={deleting}
                  startIcon={
                    deleting ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <DeleteOutlineIcon fontSize="small" />
                    )
                  }
                  onClick={() => {
                    void handleDeleteSession()
                  }}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Delete
                </Button>
              )}
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Updates live as members add their taste — vote below to lock in the
            group&apos;s pick.
          </Typography>

          {/* Owner-only: steer the whole party's candidate set mid-session. */}
          {session.isOwner && editingSearch && (
            <Box
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                mb: 2.5,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: 'background.paper',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                Adjust the search
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Location"
                  placeholder="City, neighborhood, or zip"
                  value={editLocation}
                  onChange={(e) => setEditLocation(e.target.value)}
                  fullWidth
                  size="small"
                />
                <ChipGroup
                  label="Cuisine focus"
                  options={CUISINE_OPTIONS}
                  selected={editCuisines}
                  onToggle={(v) => toggle(setEditCuisines, v)}
                />
                {searchError && (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {searchError}
                  </Alert>
                )}
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      void handleUpdateSearch()
                    }}
                    disabled={searchUpdating}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    {searchUpdating ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      'Update picks'
                    )}
                  </Button>
                  <Button
                    onClick={() => setEditingSearch(false)}
                    disabled={searchUpdating}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}

          {/* The pick — highlighted winner + reserve CTA once votes are in. */}
          {tally?.winner && winnerRec && (
            <Box
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                mb: 2.5,
                border: (theme) => `2px solid ${theme.palette.primary.main}`,
                backgroundColor: 'action.hover',
              }}
            >
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ color: 'primary.main', mb: 0.5 }}
              >
                <EmojiEventsIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Your group&apos;s pick
                </Typography>
              </Stack>
              <Typography variant="h6" fontWeight={700}>
                {winnerRec.restaurantDTO?.name ?? tally.winner}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                Leading with {voteCountFor(tally.winner)}{' '}
                {voteCountFor(tally.winner) === 1 ? 'vote' : 'votes'}
                {tally.totalVotes ? ` of ${tally.totalVotes} cast` : ''}.
              </Typography>
              {winnerReservationUrl && (
                <Button
                  variant="contained"
                  endIcon={<OpenInNewIcon />}
                  onClick={() => handleReserve(winnerRec)}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Reserve
                </Button>
              )}
            </Box>
          )}

          {coverageExpanding && (
            <Alert
              severity="info"
              icon={<PlaceOutlinedIcon fontSize="small" />}
              sx={{ borderRadius: 2, mb: 2 }}
            >
              We don&apos;t have restaurants in{' '}
              {session.location ? (
                <strong>{session.location}</strong>
              ) : (
                'this area'
              )}{' '}
              yet — we&apos;re finding some now. Your group&apos;s picks will
              appear here shortly, so check back in a moment.
            </Alert>
          )}

          {dietaryUnmatched && (
            <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
              We couldn&apos;t find a spot
              {session.location ? (
                <>
                  {' '}
                  in <strong>{session.location}</strong>
                </>
              ) : (
                ' nearby'
              )}{' '}
              that covers everyone&apos;s dietary needs. Try adjusting the
              group&apos;s dietary filters or the location.
            </Alert>
          )}

          {cuisineUnmatched && (
            <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
              We couldn&apos;t find any{' '}
              {session.searchTerm ? (
                <strong>{session.searchTerm}</strong>
              ) : (
                'matching'
              )}{' '}
              spots
              {session.location ? (
                <>
                  {' '}
                  in <strong>{session.location}</strong>
                </>
              ) : (
                ' nearby'
              )}
              . Try a different cuisine or the location.
            </Alert>
          )}

          {/* Curated shortlist — the restaurants someone actually chose, shown above the derived
              recommendations because a human pick outranks a computed one. Votable exactly like a
              recommendation (both are keyed by restaurant alias). */}
          {picks.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                Picked for the group
              </Typography>
              <Stack spacing={1.5}>
                {picks.map((pick) => {
                  const voted = yourVote === pick.restaurantAlias
                  return (
                    <Box
                      key={pick.restaurantAlias}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        p: 2,
                        borderRadius: 2,
                        border: (t) =>
                          `1px solid ${
                            voted ? t.palette.primary.main : t.palette.divider
                          }`,
                        backgroundColor: 'background.paper',
                      }}
                    >
                      <Box
                        sx={{ minWidth: 0, cursor: 'pointer' }}
                        onClick={() =>
                          navigate(
                            `${ROUTES.RESTAURANT_DETAILS}/${pick.restaurantAlias}/details`,
                          )
                        }
                      >
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {pick.name ?? pick.restaurantAlias}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          {[pick.cuisine, pick.city].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant={voted ? 'contained' : 'outlined'}
                        disabled={
                          !!joinBlockedReason ||
                          votingAlias === pick.restaurantAlias
                        }
                        onClick={() => {
                          void handleVote(pick.restaurantAlias)
                        }}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 2,
                          flexShrink: 0,
                        }}
                      >
                        {voted ? 'Your vote' : 'Vote'}
                      </Button>
                    </Box>
                  )
                })}
              </Stack>
            </Box>
          )}

          {recs.length > 0 ? (
            <Grid container spacing={2}>
              {recs.map((card, index) => (
                // Two-up only from `md` (900px), not `sm` (600px). The card's terminal action row
                // carries five icons (Plan / Reserve / Directions / Call / Share) beside the
                // "View menu →" cue, which crowds at half of a 600–900px viewport — a tablet or a
                // landscape phone. Full width below md gives that row room.
                <Grid
                  item
                  xs={12}
                  md={6}
                  key={card.restaurantDTO?.restaurantId ?? index}
                >
                  <Stack spacing={1}>
                    <div
                      style={{ height: 280, width: '100%', cursor: 'pointer' }}
                      onClick={() =>
                        navigate(
                          `${ROUTES.RESTAURANT_DETAILS}/${card?.restaurantDTO?.alias}/details`,
                        )
                      }
                    >
                      <RestaurantCard
                        id={card.restaurantDTO?.restaurantId ?? ''}
                        alias={card?.restaurantDTO?.alias ?? ''}
                        imgList={[
                          card?.restaurantDTO?.imageUrl
                            ? card.restaurantDTO.imageUrl
                            : DEFAULT_CARD_IMAGE,
                        ]}
                        title={card.restaurantDTO?.name ?? ''}
                        isVerified={card?.restaurantDTO?.verified ?? false}
                        subTitle={[
                          card?.restaurantDTO?.address1,
                          card?.restaurantDTO?.city,
                          card?.restaurantDTO?.state,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                        distance={`${card?.restaurantDTO?.distance ?? ''}`}
                        recommendationId={recommendationId}
                        phone={card?.restaurantDTO?.phone}
                        latitude={card?.restaurantDTO?.lat}
                        longitude={card?.restaurantDTO?.lng}
                        placeId={card?.restaurantDTO?.placeId}
                        address={[
                          card?.restaurantDTO?.address1,
                          card?.restaurantDTO?.city,
                          card?.restaurantDTO?.state,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                        reservationUrl={card?.restaurantDTO?.reservationUrl}
                        website={card?.restaurantDTO?.website}
                        cuisine={card?.restaurantDTO?.cuisine ?? ''}
                        city={card?.restaurantDTO?.city ?? ''}
                        matchAlwaysVisible
                        plannerShareToken={shareToken}
                        plannerSourceSurface="dine_session"
                        tagChipBgColor="liteOrange"
                        tagChipData={(() => {
                          // "Group match" = the AVERAGE of members' individual matches (a genuine
                          // "how well does this fit everyone" number). NOT `groupValue`, which is a
                          // majority-likes PROBABILITY that collapses to ~0 when everyone's
                          // individual score is low (0.05 * 0.06 ≈ 0) → the chip would vanish. Scores
                          // are already 0–100; TagChip renders `${x}%` + a quality bucket, so round
                          // to a whole percent. Fall back to the viewer's own score; suppress only
                          // when there's genuinely no score.
                          const memberScores = (card.groupRecommendations ?? [])
                            .map((m) => m?.yes)
                            .filter(
                              (y): y is number =>
                                typeof y === 'number' && Number.isFinite(y),
                            )
                          const groupAvg = memberScores.length
                            ? memberScores.reduce((a, b) => a + b, 0) /
                              memberScores.length
                            : undefined
                          const raw =
                            groupAvg ?? card.value ?? card.recommendation?.yes
                          if (
                            typeof raw !== 'number' ||
                            !Number.isFinite(raw) ||
                            raw <= 0
                          ) {
                            return []
                          }
                          const pct = Math.max(
                            0,
                            Math.min(100, Math.round(raw)),
                          )
                          return [
                            { percentage: String(pct), title: 'Group match' },
                          ]
                        })()}
                      />
                    </div>
                    {(() => {
                      const cardAlias = card?.restaurantDTO?.alias ?? ''
                      const isYourVote = !!cardAlias && yourVote === cardAlias
                      const votes = voteCountFor(cardAlias)
                      const isVoting = votingAlias === cardAlias
                      return (
                        <Button
                          fullWidth
                          variant={isYourVote ? 'contained' : 'outlined'}
                          disabled={
                            !cardAlias || isVoting || !!joinBlockedReason
                          }
                          startIcon={
                            isVoting ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : isYourVote ? (
                              <CheckCircleIcon />
                            ) : (
                              <HowToVoteOutlinedIcon />
                            )
                          }
                          onClick={() => {
                            void handleVote(cardAlias)
                          }}
                          sx={{ textTransform: 'none', borderRadius: 2 }}
                        >
                          {isYourVote ? 'Your vote' : 'Vote'}
                          {votes > 0 ? ` · ${votes}` : ''}
                        </Button>
                      )
                    })()}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          ) : picks.length > 0 ? null : (
            <EmptyScreen
              mainText={
                recsLoading
                  ? 'Finding great spots…'
                  : coverageExpanding
                  ? 'Finding restaurants near you…'
                  : dietaryUnmatched
                  ? 'No matches for everyone'
                  : cuisineUnmatched
                  ? 'No matches for that cuisine'
                  : 'No picks yet'
              }
              secondaryText={
                recsLoading
                  ? 'Hang tight while we score restaurants for your group.'
                  : coverageExpanding
                  ? 'This can take a moment for a new area. Your picks will appear here automatically.'
                  : dietaryUnmatched
                  ? "No spot here covers everyone's dietary needs. Try adjusting the dietary filters or the location."
                  : cuisineUnmatched
                  ? 'No spots here match that cuisine. Try a different cuisine or the location.'
                  : 'Add your taste to get the group started.'
              }
              imageUrl="/images/empty-screen.svg"
              containerHeight="280px"
            />
          )}

          {/* Soft signup gate at the value peak */}
          {!isAuthenticated && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 3,
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  textAlign: 'center',
                }}
              >
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Save this pick & claim your spot
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                  Sign up to lock in the group&apos;s choice and keep your taste
                  — it takes a few seconds and skips the setup.
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={() => {
                    void handleSignupCta()
                  }}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Save this pick
                </Button>
              </Box>
            </>
          )}
        </Grid>
      </Grid>

      {/* Session-scoped group chat — degrades to a sign-in nudge for pure-anon guests. */}
      <DineSessionChat
        shareToken={shareToken}
        sessionId={session.sessionId}
        sessionName={session.name}
        onSignIn={() => {
          void handleSignupCta()
        }}
      />

      {/* Lightweight in-app cues driven by the live poll (a friend joined / picks / the pick). */}
      <Snackbar
        open={!!cue}
        autoHideDuration={5000}
        onClose={() => setCue(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setCue(null)}
          sx={{ borderRadius: 2 }}
        >
          {cue}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!persistMsg}
        autoHideDuration={5000}
        onClose={() => setPersistMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={persisted ? 'success' : 'info'}
          variant="filled"
          onClose={() => setPersistMsg(null)}
          sx={{ borderRadius: 2 }}
        >
          {persistMsg}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default DineSessionPage
